import {
  createEmailMfaMethod,
  CreateEmailMfaMethodErrors,
  startVerifyEmailMfaMethod,
  StartVerifyEmailMfaMethodErrors,
  verifyEmailMfaMethod,
  VerifyEmailMfaMethodErrors,
} from "@/api/openapi";
import { PspContext } from "@/config/contexts";
import { translate } from "@/config/translator";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { MfaSetupContext } from "../../useMfaSetup";

type UseMfaInputEmailCodeParams = {
  onBack: () => void;
  onNext: () => void;
  mfaSetupContext: MfaSetupContext;
  setMfaSetupContext: Dispatch<SetStateAction<MfaSetupContext>>;
};

export function useMfaInputEmailCode({
  onBack,
  onNext,
  mfaSetupContext,
  setMfaSetupContext,
}: UseMfaInputEmailCodeParams) {
  const { lang, user, onMfaMethodCreatedRef } = useContext(PspContext);
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState<string>();
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState<
    number | null
  >(null);
  const [showWrongCodeMessage, setShowWrongCodeMessage] = useState(false);
  const resendCooldownIntervalRef = useRef<NodeJS.Timeout>(null);

  function onBackWrapper() {
    setCode("");
    setMfaSetupContext((prev) => ({ ...prev, newMethod: undefined }));
    onBack();
  }

  function sendCode(methodId: string) {
    startVerifyEmailMfaMethod({ path: { mfa_method_id: methodId } }).then(
      ({ data, error, status }) => {
        if (error) {
          if (status === 401) {
            const { detail } =
              error as StartVerifyEmailMfaMethodErrors[typeof status];
            if (detail === "fresh_token_required") {
              toast(translate("yourTokenIsNotFresh", lang));
            } else {
              toast(translate("sessionTimedOut", lang));
            }
          } else {
            toast(translate("somethingWentWrong", lang));
          }
        } else {
          setChallengeId(data);
        }
      },
    );
  }

  function onSendCodeAgain() {
    setResendCooldownSeconds(60);

    if (user) {
      if (mfaSetupContext.emailMethod) {
        sendCode(mfaSetupContext.emailMethod.id);
      } else {
        createEmailMfaMethod({
          body: { mfaType: "email", email: user?.email },
        }).then(({ data, error, status }) => {
          if (error) {
            if (status === 401) {
              const { detail } =
                error as CreateEmailMfaMethodErrors[typeof status];
              if (detail === "fresh_token_required") {
                toast(translate("yourTokenIsNotFresh", lang));
              } else {
                toast(translate("sessionTimedOut", lang));
              }
            }
            toast(translate("somethingWentWrong", lang));
          } else {
            setMfaSetupContext((prev) => ({ ...prev, emailMethod: data }));
            sendCode(data.id);
          }
        });
      }
    } else {
      toast(translate("somethingWentWrong", lang));
    }
  }

  useEffect(() => {
    onSendCodeAgain();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (resendCooldownSeconds === null) return;

    resendCooldownIntervalRef.current = setInterval(
      () =>
        setResendCooldownSeconds((prev) =>
          prev && prev > 0 ? prev - 1 : null,
        ),
      1000,
    );

    return function () {
      if (resendCooldownIntervalRef.current) {
        clearInterval(resendCooldownIntervalRef.current);
      }
    };
  }, [resendCooldownSeconds]);

  function onNextWrapper() {
    if (challengeId) {
      verifyEmailMfaMethod({ body: { code, challengeId } }).then(
        ({ data, error, status }) => {
          if (error) {
            if (status === 401) {
              const { detail } =
                error as VerifyEmailMfaMethodErrors[typeof status];
              if (detail === "fresh_token_required") {
                toast(translate("yourTokenIsNotFresh", lang));
              } else {
                toast(translate("sessionTimedOut", lang));
              }
            } else if (status === 403) {
              setShowWrongCodeMessage(true);
            } else {
              toast(translate("somethingWentWrong", lang));
            }
          } else {
            setMfaSetupContext((prev) => ({ ...prev, newMethod: data.method }));
            onNext();
          }
        },
      );
    } else {
      toast(translate("somethingWentWrong", lang));
    }
  }

  return {
    lang,
    email: user?.email,
    onBackWrapper,
    code,
    setCode,
    onSendCodeAgain,
    resendCooldownSeconds,
    onNextWrapper,
    showWrongCodeMessage,
  };
}
